import * as React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { useRootStore } from '../../../../../providers/root-store-provider';
import { TabEnumAllocations } from '../../../../../models/enums/tab-enum-allocations';
import { IconArrowDown } from '../../../../../components/app/icons/arrow-down';
import { IconArrowUp } from '../../../../../components/app/icons/arrow-up';
import { Section } from '../../../../../components/app/section/section';
import { Button } from '../../../../../components/app/form/button/button';
import { TabsMenu } from '../../../../../components/app/tabs/menu/tabs-menu';
import { TabsPanel } from '../../../../../components/app/tabs/pane/tabs-panel';
import { AllocationsUnrealizedTable } from '../../../../../components/app/tables/allocations/unrealized/allocations-unrealized-table';
import { AllocationsRealizedTable } from '../../../../../components/app/tables/allocations/realized/allocations-realized-table';
import { TotalTable } from '../../../../../components/app/tables/allocations/gain-loss/total';
import { CapitalGainLossTable } from '../../../../../components/app/tables/allocations/gain-loss/capital';
import { PeriodSwitcher } from '../../../../../components/app/period-switcher/period-switcher';

export const DashboardTradesAllocations: React.FC = observer(() => {
    const { t } = useTranslation();
    const { statisticStore } = useRootStore();
    const { ratiosStore } = statisticStore;

    const handleTabChange = useCallback(
        (tab: TabEnumAllocations) => {
            ratiosStore.setTab(tab);
        },
        [ratiosStore],
    );

    const tabsMenu = [
        {
            label: t('pages.index.allocations.tabs.unrealizedTrades'),
            value: TabEnumAllocations.Unrealized,
        },
        {
            label: t('pages.index.allocations.tabs.realizedTrades'),
            value: TabEnumAllocations.Realized,
        },
        {
            label: t('pages.index.allocations.tabs.capitalGainLossUnified'),
            value: TabEnumAllocations.CapitalGainLoss,
        },
        {
            label: t('pages.index.allocations.tabs.totalGainLoss'),
            value: TabEnumAllocations.TotalGainLoss,
        },
    ];

    return (
        <Section
            title={t('pages.index.performanceOfIndividualAssets')}
            action={
                <PeriodSwitcher
                    value={statisticStore.ratiosType}
                    onChange={(periodType) => statisticStore.setRatiosType(periodType)}
                />
            }
            footer={
                <>
                    {ratiosStore.canDisplayMore && (
                        <Button
                            variant={'secondary'}
                            size={'small'}
                            icon={ratiosStore.isExpanded ? <IconArrowDown /> : <IconArrowUp />}
                            onClick={() => {
                                ratiosStore.toggleAllocationsExpanded();
                            }}
                        >
                            {ratiosStore.isExpanded ? t('pages.index.viewMore') : t('pages.index.viewLess')}
                        </Button>
                    )}
                </>
            }
        >
            <div className="max-vl:my-small">
                <TabsMenu variant="cards" onChange={handleTabChange} value={ratiosStore.currentTab} items={tabsMenu} />
            </div>
            <TabsPanel activeTab={ratiosStore.currentTab} value={TabEnumAllocations.Unrealized}>
                <AllocationsUnrealizedTable />
            </TabsPanel>
            <TabsPanel activeTab={ratiosStore.currentTab} value={TabEnumAllocations.Realized}>
                <AllocationsRealizedTable />
            </TabsPanel>
            <TabsPanel activeTab={ratiosStore.currentTab} value={TabEnumAllocations.CapitalGainLoss}>
                <CapitalGainLossTable />
            </TabsPanel>
            <TabsPanel activeTab={ratiosStore.currentTab} value={TabEnumAllocations.TotalGainLoss}>
                <TotalTable />
            </TabsPanel>
        </Section>
    );
});
